import type { Permission } from '@edms/contracts';
import { Loader2 } from 'lucide-react';
import { type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { EmptyState } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { AppShell } from '@/layout/app-shell';
import { AccountPage } from '@/pages/account-page';
import { AuditPage } from '@/pages/admin/audit-page';
import { BranchesPage } from '@/pages/admin/branches-page';
import { OcrZonesPage } from '@/pages/admin/ocr-zones-page';
import { SettingsPage } from '@/pages/admin/settings-page';
import { UsersPage } from '@/pages/admin/users-page';
import { DashboardPage } from '@/pages/dashboard-page';
import { DuplicatesPage } from '@/pages/duplicates-page';
import { LoginPage } from '@/pages/login-page';
import { LotDetailPage } from '@/pages/lot-detail-page';
import { LotsPage } from '@/pages/lots-page';
import { ReviewPage } from '@/pages/review-page';
import { SearchPage } from '@/pages/search-page';

export function App(): React.JSX.Element {
  const { status, user } = useAuth();

  if (status === 'checking') {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-3 text-ink-faint">
          <Loader2 className="size-5 animate-spin" aria-hidden />
          <p className="text-xs">Ouverture de la session…</p>
        </div>
      </div>
    );
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/connexion" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/connexion" replace />} />
      </Routes>
    );
  }

  // Un mot de passe temporaire doit être renouvelé avant tout accès : le
  // laisser en circulation viderait de son sens la remise hors ligne du mot de
  // passe initial (§16).
  if (user?.mustChangePassword) {
    return (
      <Routes>
        <Route path="/mon-compte" element={<AccountPage forced />} />
        <Route path="*" element={<Navigate to="/mon-compte" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/connexion" element={<Navigate to="/" replace />} />

      <Route element={<AppShell />}>
        <Route index element={<LandingRoute />} />
        <Route path="recherche" element={<Guarded permission="receipts:read" element={<SearchPage />} />} />
        <Route path="recherche/:receiptId" element={<Guarded permission="receipts:read" element={<SearchPage />} />} />

        <Route path="numerisation" element={<Guarded permission="lots:read" element={<LotsPage />} />} />
        <Route path="numerisation/lots/:lotId" element={<Guarded permission="lots:read" element={<LotDetailPage />} />} />

        <Route path="exceptions/validation" element={<Guarded permission="receipts:correct" element={<ReviewPage />} />} />
        <Route path="exceptions/doublons" element={<Guarded permission="duplicates:resolve" element={<DuplicatesPage />} />} />

        <Route path="administration/utilisateurs" element={<Guarded permission="users:read" element={<UsersPage />} />} />
        <Route path="administration/bureaux" element={<Guarded permission="branches:read" element={<BranchesPage />} />} />
        <Route path="administration/zones-ocr" element={<Guarded permission="ocr-templates:read" element={<OcrZonesPage />} />} />
        <Route path="administration/journal" element={<Guarded permission="audit:read" element={<AuditPage />} />} />
        <Route path="administration/parametres" element={<Guarded permission="settings:read" element={<SettingsPage />} />} />

        <Route path="mon-compte" element={<AccountPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/**
 * Écran d'accueil.
 *
 * Le tableau de bord n'est pas accessible à tous : un compte de consultation
 * n'y a pas droit. Plutôt que de l'accueillir par un refus, on l'amène
 * directement là où son travail commence — la recherche.
 */
function LandingRoute(): React.JSX.Element {
  const { can } = useAuth();

  if (can('dashboard:read')) return <DashboardPage />;
  if (can('receipts:read')) return <Navigate to="/recherche" replace />;

  return (
    <EmptyState
      title="Aucun écran accessible"
      description="Votre compte ne dispose d'aucune permission de consultation. Contactez votre administrateur."
      className="h-full"
    />
  );
}

/**
 * Garde de route.
 *
 * Redouble le contrôle serveur, qui reste la seule autorité : ce garde évite
 * d'afficher un écran qui échouerait de toute façon, il ne protège rien à lui
 * seul.
 */
function Guarded({ permission, element }: { permission: Permission; element: ReactNode }): React.JSX.Element {
  const { can } = useAuth();

  if (!can(permission)) {
    return (
      <EmptyState
        title="Accès non autorisé"
        description="Votre profil ne donne pas accès à cet écran. Contactez votre administrateur si vous pensez qu'il s'agit d'une erreur."
        className="h-full"
      />
    );
  }

  return <>{element}</>;
}

function NotFound(): React.JSX.Element {
  const location = useLocation();

  return (
    <EmptyState
      title="Page introuvable"
      description={`Aucun écran ne correspond à « ${location.pathname} ».`}
      className="h-full"
    />
  );
}
